import { Controller } from "@hotwired/stimulus"

// Steps on one form (plan-first onboarding U5, KTD3): one panel on screen,
// Next and Back between them, and the panel the server names open on
// connect (a refused save re-opens the step that owns the first error).
// Generic: it knows panels, titles, a Next button to gate and the form's
// submit, nothing about what the panels hold. The gate is mirrored from
// the submit's disabled attribute, which the split controller owns, so the
// two never have to talk.
//
// Panels: [data-steps-target="step"][data-steps-name="N"], numbered from 1
// in order. Each holds a title with tabindex="-1" that takes focus when the
// panel opens. Enter in a field on any panel but the last is treated as
// Next, so only the last panel's submit posts the form.
export default class extends Controller {
  static targets = ["step", "title", "next", "save"]
  static values = { open: { type: Number, default: 1 } }

  connect() {
    // Only an error re-render names a step: then the title takes focus,
    // as it would after Next. A fresh page leaves focus where it was.
    this.show(this.openValue, { focus: this.element.hasAttribute(`data-${this.identifier}-open-value`) })

    this.mirror()
    if (this.hasSaveTarget) {
      this.observer = new MutationObserver(() => this.mirror())
      this.observer.observe(this.saveTarget, { attributes: true, attributeFilter: ["disabled"] })
    }
  }

  disconnect() {
    this.observer?.disconnect()
  }

  next() {
    this.show(this.current + 1)
  }

  back() {
    this.show(this.current - 1)
  }

  // Enter in a field: Next on every panel but the last, never a post. On
  // the last panel the browser's own submission stands.
  enter(event) {
    if (event.key !== "Enter" || !(event.target instanceof HTMLInputElement)) return
    if (this.current >= this.stepTargets.length) return

    event.preventDefault()
    const button = this.panel(this.current)?.querySelector('button[data-action~="steps#next"]')
    if (button && !button.disabled) this.next()
  }

  show(name, { focus = true } = {}) {
    const last = this.stepTargets.length
    const number = Math.min(Math.max(Number(name) || 1, 1), last)
    this.stepTargets.forEach((panel) => { panel.hidden = Number(panel.dataset.stepsName) !== number })
    if (!focus) return

    const title = this.titleTargets.find((element) => this.panel(number)?.contains(element))
    title?.focus()
  }

  // Next waits for what the submit waits for.
  mirror() {
    const disabled = this.hasSaveTarget && this.saveTarget.disabled
    this.nextTargets.forEach((button) => { button.disabled = disabled })
  }

  get current() {
    const open = this.stepTargets.find((panel) => !panel.hidden)
    return open ? Number(open.dataset.stepsName) : 1
  }

  panel(number) {
    return this.stepTargets.find((panel) => Number(panel.dataset.stepsName) === number)
  }
}
